import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { PlusOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import Table from '@/components/ui/table/Table';
import StatusBadge from '@/components/ui/StatusBadge';
import { useProjectCollaboration } from '@hooks/projects/useProjectCollaboration';
import InviteMemberModal from '@components/module/projects/contributors_tab/InviteMemberModal';
import InviteTenantModal from './InviteTenantModal';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    MutedText,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
} from '@/pages/inside/tenant/style';

/**
 * Cross-tenant collaboration list: invite companies, accept/reject, assign members.
 */
export default function CollaborationPanel({ projectId }) {
    const {
        canView,
        canInvite,
        canAssign,
        tableData,
        collaborationTableColumns,
        isLoading,
        inviteTenantModalOpen,
        openInviteTenantModal,
        closeInviteTenantModal,
        inviteTenantForm,
        submitInviteTenant,
        inviteTenantPending,
        assignModalOpen,
        closeAssignModal,
        assignForm,
        assignSelectOptions,
        filterSelectOption,
        submitAssignMembers,
        assignMembersPending,
        acceptInvite,
        rejectInvite,
        acceptPending,
        rejectPending,
        openAssignModal,
        isIncomingPending,
    } = useProjectCollaboration(projectId);

    const tableColumns = useMemo(() => {
        const base = collaborationTableColumns.map((col, idx) =>
            idx === 1 ? { ...col, render: (status) => <StatusBadge status={status} size="small" /> } : col,
        );
        base.push({
            title: 'Actions',
            key: 'actions',
            width: 220,
            render: (_, record) => {
                if (record.isIncomingPending || isIncomingPending(record)) {
                    return (
                        <Container $display="flex" $gap="8px" $flexWrap="wrap">
                            <Button
                                type="button"
                                $size="12px"
                                $padding="4px 10px"
                                loading={acceptPending}
                                onClick={() => acceptInvite(record.id)}
                            >
                                Accept
                            </Button>
                            <Button
                                type="button"
                                $size="12px"
                                $padding="4px 10px"
                                loading={rejectPending}
                                onClick={() => rejectInvite(record.id)}
                            >
                                Reject
                            </Button>
                        </Container>
                    );
                }
                if (record.status === 'accepted') {
                    return (
                        <Button
                            type="button"
                            $size="12px"
                            $padding="4px 10px"
                            icon={<UserAddOutlined />}
                            onClick={() => openAssignModal(record)}
                            permissionDenied={!canAssign}
                        >
                            Assign members
                        </Button>
                    );
                }
                return <MutedText style={{ margin: 0, fontSize: 12 }}>—</MutedText>;
            },
        });
        return base;
    }, [
        collaborationTableColumns,
        acceptInvite,
        rejectInvite,
        acceptPending,
        rejectPending,
        openAssignModal,
        canAssign,
        isIncomingPending,
    ]);

    const inviteButton = (
        <Button
            type="button"
            $primary
            $blackOnLight
            icon={<PlusOutlined />}
            onClick={openInviteTenantModal}
            $size="13px"
            $padding="8px 16px"
            permissionDenied={!canInvite}
        >
            Invite company
        </Button>
    );

    if (!canView) {
        return (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <TeamOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>You don&apos;t have permission to view collaboration</EmptyStateTitle>
                <EmptyStateDescription>Contact your admin if you need access.</EmptyStateDescription>
            </EmptyStateBlock>
        );
    }

    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }

    return (
        <>
            <BoardTabRoot
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                    overflow: 'hidden',
                }}
            >
                <Container $display="flex" $flexDirection="column" $gap="4px" $marginBottom="20px">
                    <BoardPageTitle>Collaboration</BoardPageTitle>
                    <BoardPageSubtitle>
                        Invite partner companies and assign their users to this project.
                    </BoardPageSubtitle>
                </Container>

                <Container
                    $display="flex"
                    $justifyContent="space-between"
                    $alignItems="center"
                    $flexWrap="wrap"
                    $gap="12px"
                    $marginBottom="12px"
                >
                    <MutedText style={{ margin: 0, fontSize: 13 }}>
                        {tableData.length} collaboration{tableData.length !== 1 ? 's' : ''}
                    </MutedText>
                    {inviteButton}
                </Container>

                {tableData.length === 0 ? (
                    <EmptyStateBlock>
                        <EmptyStateIcon>
                            <TeamOutlined />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No collaborations yet</EmptyStateTitle>
                        <EmptyStateDescription>
                            Invite another company to collaborate on this project.
                        </EmptyStateDescription>
                        <div style={{ marginTop: 16 }}>{inviteButton}</div>
                    </EmptyStateBlock>
                ) : (
                    <Table
                        columns={tableColumns}
                        dataSource={tableData}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                )}
            </BoardTabRoot>

            <InviteTenantModal
                open={inviteTenantModalOpen}
                onClose={closeInviteTenantModal}
                form={inviteTenantForm}
                onSubmit={submitInviteTenant}
                pending={inviteTenantPending}
                permissionDenied={!canInvite}
            />

            <InviteMemberModal
                open={assignModalOpen}
                onCancel={closeAssignModal}
                form={assignForm}
                inviteSelectOptions={assignSelectOptions}
                filterSelectOption={filterSelectOption}
                onSubmit={submitAssignMembers}
                addContributorsPending={assignMembersPending}
            />
        </>
    );
}
